// Pixi.js v8 galaxy map (GDD 08 screen 1, spec 15c): gates, systems, labels, stationed ships, fleets
// in flight and the selection as layers; the zoom tier only toggles what is visible. Drag to pan,
// wheel / pinch to zoom, tap to select a system (or, with no system near, a fleet). Labels, ship rows
// and type icons are decluttered whenever the zoom or the selection changes.
import type { MapResponse, MapSystem, ShipCounts } from '@stars-and-ships/api-types';
import { Application, Container } from 'pixi.js';
import { type Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { MAP_COLORS } from '../theme';
import { boundsOf, type Camera, centerOn, fitCamera, type Point, panBy, zoomAt } from './camera';
import { declutter, labelPriority, mergeCandidates } from './declutter';
import type { MapFleet } from './fleets';
import { type FleetsLayer, fleetsLayer } from './layers/fleets';
import { type GarrisonsLayer, garrisonsLayer } from './layers/garrisons';
import { gatesLayer } from './layers/gates';
import { type LabelsLayer, labelsLayer } from './layers/labels';
import { type SelectionLayer, selectionLayer } from './layers/selection';
import { type SystemsLayer, systemsLayer } from './layers/systems';
import { type MapSelection, pickTap } from './pick';
import { FOCUS_SCALE, zoomTier } from './zoomTier';

export { systemColor } from './markers';
export const COLORS = MAP_COLORS;

export type { MapSelection } from './pick';

export interface GalaxyMapHandle {
  /** Centres `point` at `scale` (default FOCUS_SCALE); queued until the renderer is ready. */
  focus(point: Point, scale?: number): void;
  /** Zooms around the viewport centre. */
  zoomBy(factor: number): void;
  fit(): void;
}

export interface GalaxyMapProps {
  map: MapResponse;
  playerId: string | undefined;
  /** The viewer's alliance: members' systems get the ally colour. */
  allianceId?: string | null | undefined;
  homeSystemId: string | null;
  ownSystemIds: ReadonlySet<string>;
  garrisons: ReadonlyMap<string, ShipCounts>;
  fleets: readonly MapFleet[];
  selection: MapSelection;
  onSelect(selection: MapSelection): void;
  ref?: Ref<GalaxyMapHandle>;
}

const TAP_SLOP_PX = 6;

interface Slots {
  world: Container;
  gates: Container;
  systems: Container;
  labels: Container;
  garrisons: Container;
  fleets: Container;
  selection: Container;
}

const replace = (slot: Container, child: Container) => {
  for (const old of slot.removeChildren()) old.destroy({ children: true });
  slot.addChild(child);
};

export function GalaxyMap({
  map,
  playerId,
  allianceId,
  homeSystemId,
  ownSystemIds,
  garrisons,
  fleets,
  selection,
  onSelect,
  ref,
}: GalaxyMapProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const appRef = useRef<Application | null>(null);
  const slotsRef = useRef<Slots | null>(null);
  const cameraRef = useRef<Camera | null>(null);
  const pendingFocusRef = useRef<{ point: Point; scale: number } | null>(null);
  const layersRef = useRef<{
    systems?: SystemsLayer;
    labels?: LabelsLayer;
    garrisons?: GarrisonsLayer;
    selection?: SelectionLayer;
    fleets?: FleetsLayer;
  }>({});
  const fleetsRef = useRef(fleets);
  fleetsRef.current = fleets;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const viewerRef = useRef({ playerId, allianceId });
  viewerRef.current = { playerId, allianceId };
  /** Scale and selection of the last declutter; null after a layer is rebuilt. */
  const declutteredRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  // Create the renderer and the layer slots once.
  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;
    const app = new Application();
    let cancelled = false;
    let initialized = false;
    void app
      .init({
        canvas,
        resizeTo: host,
        antialias: true,
        backgroundAlpha: 0,
        autoDensity: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        preference: 'webgl',
      })
      .then(() => {
        initialized = true;
        if (cancelled) {
          app.destroy();
          return;
        }
        const slots: Slots = {
          world: new Container(),
          gates: new Container(),
          systems: new Container(),
          labels: new Container(),
          garrisons: new Container(),
          fleets: new Container(),
          selection: new Container(),
        };
        slots.world.addChild(
          slots.gates,
          slots.systems,
          slots.fleets,
          slots.labels,
          slots.garrisons,
          slots.selection
        );
        app.stage.addChild(slots.world);
        appRef.current = app;
        slotsRef.current = slots;
        setReady(true);
      });
    return () => {
      cancelled = true;
      if (initialized) app.destroy();
      appRef.current = null;
      slotsRef.current = null;
    };
  }, []);

  const applyCamera = useCallback((camera: Camera) => {
    cameraRef.current = camera;
    const world = slotsRef.current?.world;
    world?.position.set(camera.x, camera.y);
    world?.scale.set(camera.scale);
    const tier = zoomTier(camera.scale);
    const layers = layersRef.current;
    layers.systems?.setView(tier, camera.scale);
    layers.labels?.setView(tier, camera.scale);
    layers.garrisons?.setView(tier, camera.scale);
    layers.selection?.setView(camera.scale);
    const selected = selectionRef.current?.kind === 'system' ? selectionRef.current.id : null;
    const key = `${camera.scale}|${selected}`;
    if (key === declutteredRef.current || !layers.labels || !layers.systems) return;
    declutteredRef.current = key;
    const viewer = { ...viewerRef.current, selectedId: selected };
    const priority = (system: MapSystem) => labelPriority(system, viewer);
    const labels = mergeCandidates([
      ...layers.labels.candidates(priority),
      ...(layers.garrisons?.candidates(priority) ?? []),
    ]);
    const shown = declutter(
      [...labels, ...layers.systems.candidates()],
      layers.systems.obstacles()
    );
    layers.labels.applyShown(shown);
    layers.garrisons?.applyShown(shown);
    layers.systems.applyShown(shown);
  }, []);

  const viewport = useCallback(() => {
    const screen = appRef.current?.screen;
    return { width: screen?.width ?? 1, height: screen?.height ?? 1 };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus(point, scale = FOCUS_SCALE) {
        if (!appRef.current || !cameraRef.current) {
          pendingFocusRef.current = { point, scale };
          return;
        }
        applyCamera(centerOn(point, viewport(), scale));
      },
      zoomBy(factor) {
        const camera = cameraRef.current;
        if (!camera) return;
        const v = viewport();
        applyCamera(zoomAt(camera, { x: v.width / 2, y: v.height / 2 }, factor));
      },
      fit() {
        if (appRef.current) applyCamera(fitCamera(boundsOf(map.systems), viewport()));
      },
    }),
    [applyCamera, viewport, map.systems]
  );

  // Static layers: rebuilt when the map or the viewer's systems change.
  useEffect(() => {
    const slots = slotsRef.current;
    if (!ready || !slots) return;
    replace(slots.gates, gatesLayer(map));
    const systems = systemsLayer({
      systems: map.systems,
      playerId,
      allianceId,
      homeSystemId,
      ownSystemIds,
    });
    replace(slots.systems, systems.container);
    const labels = labelsLayer({ map, playerId });
    replace(slots.labels, labels.container);
    layersRef.current.systems = systems;
    layersRef.current.labels = labels;
    declutteredRef.current = null;
    const pending = pendingFocusRef.current;
    pendingFocusRef.current = null;
    applyCamera(
      pending
        ? centerOn(pending.point, viewport(), pending.scale)
        : (cameraRef.current ?? fitCamera(boundsOf(map.systems), viewport()))
    );
  }, [ready, map, playerId, allianceId, homeSystemId, ownSystemIds, applyCamera, viewport]);

  // Stationed ships: rebuilt when the own fleets change.
  useEffect(() => {
    const slots = slotsRef.current;
    if (!ready || !slots) return;
    const layer = garrisonsLayer({ systems: map.systems, garrisons });
    replace(slots.garrisons, layer.container);
    layersRef.current.garrisons = layer;
    declutteredRef.current = null;
    if (cameraRef.current) applyCamera(cameraRef.current);
  }, [ready, map, garrisons, applyCamera]);

  // Selection ring for a selected system.
  useEffect(() => {
    const slots = slotsRef.current;
    if (!ready || !slots) return;
    const system =
      selection?.kind === 'system' ? map.systems.find((s) => s.id === selection.id) : undefined;
    const layer = selectionLayer(system);
    replace(slots.selection, layer.container);
    layersRef.current.selection = layer;
    // The selected system's label wins its spot, so the declutter runs again.
    if (cameraRef.current) applyCamera(cameraRef.current);
  }, [ready, map, selection, applyCamera]);

  // Fleets in flight: redrawn every frame (moving markers, drifting dashes, pulse).
  useEffect(() => {
    const app = appRef.current;
    const slots = slotsRef.current;
    if (!ready || !app || !slots) return;
    const layer = fleetsLayer();
    replace(slots.fleets, layer.container);
    layersRef.current.fleets = layer;
    const tick = () => {
      const camera = cameraRef.current;
      if (!camera) return;
      const selected = selectionRef.current;
      layer.update(
        fleetsRef.current,
        selected?.kind === 'fleet' ? selected.id : null,
        Date.now(),
        camera.scale
      );
    };
    app.ticker.add(tick);
    return () => {
      // The renderer effect's cleanup may already have destroyed the app (ticker = null).
      app.ticker?.remove(tick);
    };
  }, [ready]);

  // Pointer input: pan, pinch, wheel zoom and tap selection.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!ready || !canvas) return;
    const pointers = new Map<number, { x: number; y: number }>();
    let moved = false;
    let start = { x: 0, y: 0 };
    const local = (e: PointerEvent | WheelEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onDown = (e: PointerEvent) => {
      canvas.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, local(e));
      if (pointers.size === 1) {
        moved = false;
        start = local(e);
      }
    };
    const onMove = (e: PointerEvent) => {
      const previous = pointers.get(e.pointerId);
      const camera = cameraRef.current;
      if (!previous || !camera) return;
      const current = local(e);
      if (pointers.size === 1) {
        if (Math.hypot(current.x - start.x, current.y - start.y) > TAP_SLOP_PX) moved = true;
        applyCamera(panBy(camera, current.x - previous.x, current.y - previous.y));
      } else if (pointers.size === 2) {
        moved = true;
        const other = [...pointers.entries()].find(([id]) => id !== e.pointerId)?.[1];
        if (other) {
          const before = Math.hypot(previous.x - other.x, previous.y - other.y);
          const after = Math.hypot(current.x - other.x, current.y - other.y);
          const mid = { x: (current.x + other.x) / 2, y: (current.y + other.y) / 2 };
          if (before > 0) applyCamera(zoomAt(camera, mid, after / before));
        }
      }
      pointers.set(e.pointerId, current);
    };
    const onUp = (e: PointerEvent) => {
      const wasSingle = pointers.size === 1;
      pointers.delete(e.pointerId);
      const camera = cameraRef.current;
      if (!wasSingle || moved || !camera) return;
      onSelectRef.current(pickTap(map.systems, fleetsRef.current, camera, local(e), Date.now()));
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const camera = cameraRef.current;
      if (camera) applyCamera(zoomAt(camera, local(e), Math.exp(-e.deltaY * 0.0015)));
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('wheel', onWheel);
    };
  }, [ready, map, applyCamera]);

  return (
    <div ref={hostRef} className="absolute inset-0 overflow-hidden">
      <canvas
        id="galaxy-canvas"
        ref={canvasRef}
        className="block size-full touch-none"
        aria-label="Galaxy map"
      />
    </div>
  );
}
